import { z } from 'zod';
const ZIP = [0x50, 0x4b, 0x03, 0x04];
/**
 * Keyed by MIME type, because that is what a step definition's `acceptedTypes` carries and
 * what the browser reports.
 */
export const uploadKinds = {
    'application/pdf': {
        mime: 'application/pdf',
        label: 'PDF',
        extensions: ['.pdf'],
        signatures: [[0x25, 0x50, 0x44, 0x46]], // %PDF
    },
    'image/jpeg': {
        mime: 'image/jpeg',
        label: 'JPEG photo',
        extensions: ['.jpg', '.jpeg'],
        signatures: [[0xff, 0xd8, 0xff]],
    },
    'image/png': {
        mime: 'image/png',
        label: 'PNG image',
        extensions: ['.png'],
        signatures: [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
    },
    'image/heic': {
        mime: 'image/heic',
        label: 'iPhone photo',
        extensions: ['.heic', '.heif'],
        // ISO-BMFF: a four-byte box length, then `ftyp`, then the brand. The length varies, so
        // the first four bytes are wildcards. iPhones write heic, heix, hevc, mif1 and msf1 —
        // refusing any of them would refuse a photo the person took correctly.
        signatures: ['heic', 'heix', 'hevc', 'heim', 'heis', 'hevm', 'mif1', 'msf1'].map((brand) => [
            null,
            null,
            null,
            null,
            0x66,
            0x74,
            0x79,
            0x70,
            ...Array.from(brand, (character) => character.charCodeAt(0)),
        ]),
    },
    'application/msword': {
        mime: 'application/msword',
        label: 'Word document (.doc)',
        extensions: ['.doc'],
        signatures: [[0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]], // OLE2 compound file
    },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        label: 'Word document (.docx)',
        extensions: ['.docx'],
        // A .docx IS a zip. The signature can only prove it is a zip container, which is why
        // stored content type is rewritten to octet-stream regardless — see rewriteContentType.
        signatures: [ZIP],
    },
};
export const uploadMimeSchema = z.enum(Object.keys(uploadKinds));
export function isUploadMime(value) {
    return Object.prototype.hasOwnProperty.call(uploadKinds, value);
}
/** The `accept` attribute for a step's configured types — MIME plus extensions, since Android's picker honours extensions more reliably than MIME. */
export function acceptAttribute(acceptedTypes) {
    const parts = [];
    for (const type of acceptedTypes) {
        if (!isUploadMime(type))
            continue;
        parts.push(type, ...uploadKinds[type].extensions);
    }
    return parts.join(',');
}
export function describeAccepted(acceptedTypes) {
    const labels = acceptedTypes.filter(isUploadMime).map((type) => uploadKinds[type].label);
    if (labels.length === 0)
        return 'no file types';
    if (labels.length === 1)
        return labels[0];
    return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}
/**
 * The single allowlist gate. Called in the browser before the person waits for an upload,
 * and called again in the API before a SAS is minted — the browser's run is a courtesy,
 * the API's is the gate.
 *
 * The messages are written for the candidate, not for a log: a refusal has to tell someone
 * on a phone what to do next, and "415 unsupported media type" does not.
 */
export function checkDeclaredUpload(declared, limits) {
    if (!isUploadMime(declared.contentType) || !limits.acceptedTypes.includes(declared.contentType)) {
        return {
            ok: false,
            reason: 'type-not-accepted',
            message: `That file type will not work here. Please send a ${describeAccepted(limits.acceptedTypes)}.`,
        };
    }
    if (declared.byteLength <= 0) {
        return { ok: false, reason: 'empty', message: 'That file is empty. Please pick it again.' };
    }
    if (declared.byteLength > limits.maxSizeMb * 1024 * 1024) {
        return {
            ok: false,
            reason: 'too-large',
            message: `That file is too big. The limit is ${limits.maxSizeMb} MB — a photo taken at a lower resolution usually fits.`,
        };
    }
    return { ok: true, mime: declared.contentType };
}
/**
 * Do the bytes match what was declared?
 *
 * Server-side only, on the real stored bytes, after the upload lands. This is the check the
 * declared content type cannot substitute for: anyone can `PUT` an HTML file with
 * `content-type: application/pdf`, and only the first bytes say otherwise.
 */
export function bytesMatchDeclaredType(head, mime) {
    return uploadKinds[mime].signatures.some((signature) => {
        if (head.length < signature.length)
            return false;
        return signature.every((byte, index) => byte === null || head[index] === byte);
    });
}
/** Enough bytes for the longest signature, with room to spare. */
export const SNIFF_BYTES = 32;
/**
 * What a staged blob's content type is rewritten to once it is accepted.
 *
 * Never the declared type. A stored `.html` served as `text/html` from the storage
 * account's own origin is stored XSS with a candidate's session behind it; `.docx` and
 * `.pdf` served inline are the same class of problem with more steps. Nothing in this
 * system ever needs the browser to render a staged file — HR reviews it through a
 * download — so the safe value is the only value.
 */
export const STAGED_CONTENT_TYPE = 'application/octet-stream';
export const STAGED_CONTENT_DISPOSITION = 'attachment';
//# sourceMappingURL=allowlist.js.map